import { Draggable } from '@hello-pangea/dnd';
import {
  ActionIcon,
  Badge,
  Box,
  Button,
  Group,
  Stack,
  TagsInput,
  Text,
  TextInput
} from '@mantine/core';
import { IconCategoryPlus, IconTrash } from '@tabler/icons-react';
import { Question } from 'pages/app/LIA/AssessmentSchedules/utils/schema';
import React, { useEffect, useState } from 'react';

/* eslint-disable */
interface QuestionItemProps {
  question: Question;
  index: number;
  moduleId: string;
  respType: string;
  externalTypeId?: string;
  isReset?: 'Y' | 'N' | 'R';
  onRemove: (
    questionId: string,
    respondentType: string,
    moduleId: string,
    externalTypeId?: string
  ) => void;
  onCategoryChange?: (
    questionId: string,
    respondentType: string,
    moduleId: string,
    category: string,
    skalaCategory?: string,
    externalTypeId?: string
  ) => void;
}
/* eslint-enable */

const QuestionItem: React.FC<QuestionItemProps> = ({
  question,
  index,
  moduleId,
  respType,
  externalTypeId,
  isReset = 'Y',
  onRemove,
  onCategoryChange
}) => {
  const [showCategoryInput, setShowCategoryInput] = useState(false);
  const [categoryValue, setCategoryValue] = useState(
    question.category ? question.category.split(',').map((c) => c.trim()) : []
  );
  const [skalaCategoryValue, setSkalaCategoryValue] = useState(question.skalaCategory || '');
  const [showSkalaCategoryInput, setShowSkalaCategoryInput] = useState(false);

  useEffect(() => {
    setCategoryValue(question.category ? question.category.split(',').map((c) => c.trim()) : []);
    setSkalaCategoryValue(question.skalaCategory || '');
  }, [question.category, question.skalaCategory]);

  const handleCategorySubmit = () => {
    if (onCategoryChange) {
      const categoryString = Array.isArray(categoryValue) ? categoryValue.join(', ') : '';

      onCategoryChange(
        question.id,
        respType,
        moduleId,
        categoryString,
        skalaCategoryValue, // Keep existing skalaCategory
        externalTypeId
      );
    }
    setShowCategoryInput(false);
  };

  const handleSkalaCategorySubmit = () => {
    if (onCategoryChange) {
      const categoryString = Array.isArray(categoryValue)
        ? categoryValue.join(', ')
        : question.category || '';

      onCategoryChange(
        question.id,
        respType,
        moduleId,
        categoryString,
        skalaCategoryValue,
        externalTypeId
      );
    }
    setShowSkalaCategoryInput(false);
  };

  return (
    <Draggable key={question.id} draggableId={question.id} index={index}>
      {(provided, snapshot) => (
        <div
          ref={provided.innerRef}
          {...provided.draggableProps}
          {...provided.dragHandleProps}
          style={{
            ...provided.draggableProps.style,
            opacity: snapshot.isDragging ? 0.8 : 1
          }}
        >
          <Box
            p='md'
            mb='md'
            style={{
              backgroundColor: '#f8f9fa',
              borderRadius: '8px',
              border: '1px solid #eee',
              cursor: 'grab'
            }}
          >
            <Group gap={8} align='flex-start'>
              <Text fw={500} size='md' color='#333'>
                {index + 1}.
              </Text>
              <Stack gap={4} style={{ flex: 1 }}>
                <Group justify='justify-between' align='flex-start'>
                  <Text fw={600} size='md' color='#333'>
                    {question.title}
                  </Text>
                </Group>

                {showCategoryInput && (
                  <Group mb={8}>
                    <TagsInput
                      placeholder='Add categories and press Enter'
                      value={Array.isArray(categoryValue) ? categoryValue : []}
                      onChange={(value) => setCategoryValue(value)}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter') {
                          e.preventDefault();
                          handleCategorySubmit();
                        } else if (e.key === 'Escape') {
                          setShowCategoryInput(false);
                        }
                      }}
                      autoFocus
                      style={{ flex: 1 }}
                      maxTags={10}
                      clearable
                    />
                    <Button size='xs' onClick={handleCategorySubmit} color='grape'>
                      Save
                    </Button>
                    <Button size='xs' variant='subtle' onClick={() => setShowCategoryInput(false)}>
                      Cancel
                    </Button>
                  </Group>
                )}

                {showSkalaCategoryInput && (
                  <Group mb={8}>
                    <TextInput
                      placeholder='Enter skala category'
                      value={skalaCategoryValue}
                      onChange={(e) => setSkalaCategoryValue(e.currentTarget.value)}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter') {
                          handleSkalaCategorySubmit();
                        } else if (e.key === 'Escape') {
                          setShowSkalaCategoryInput(false);
                        }
                      }}
                      autoFocus
                      style={{ flex: 1 }}
                    />
                    <Button size='xs' onClick={handleSkalaCategorySubmit} color='indigo'>
                      Save
                    </Button>
                    <Button
                      size='xs'
                      variant='subtle'
                      onClick={() => setShowSkalaCategoryInput(false)}
                    >
                      Cancel
                    </Button>
                  </Group>
                )}

                <Group>
                  {!showCategoryInput && (
                    <>
                      {question.category ? (
                        <Group gap={4} wrap='wrap'>
                          {question.category.split(',').map((cat, idx) => (
                            <Badge
                              key={idx}
                              color='grape'
                              onClick={() => {
                                setCategoryValue(
                                  question.category!.split(',').map((c) => c.trim())
                                );
                                setShowCategoryInput(true);
                              }}
                              style={{ cursor: 'pointer' }}
                              variant='light'
                              size='sm'
                            >
                              {cat.trim()}
                            </Badge>
                          ))}
                          <Button
                            variant='subtle'
                            color='grape'
                            size='xs'
                            onClick={() => {
                              setCategoryValue(question.category!.split(',').map((c) => c.trim()));
                              setShowCategoryInput(true);
                            }}
                            style={{ cursor: 'pointer' }}
                            leftSection={<IconCategoryPlus size={16} />}
                          >
                            <Text size='xs' fw={'bold'}>
                              Edit Categories
                            </Text>
                          </Button>
                        </Group>
                      ) : (
                        <Button
                          variant='subtle'
                          color='grape'
                          size='xs'
                          onClick={() => setShowCategoryInput(true)}
                          style={{ cursor: 'pointer' }}
                          leftSection={<IconCategoryPlus size={16} />}
                        >
                          <Text size='xs' fw={'bold'}>
                            Add Categories
                          </Text>
                        </Button>
                      )}

                      {/* Skala Category Badge/Button */}
                      {question.skalaCategory ? (
                        <Badge
                          color='indigo'
                          onClick={() => {
                            setSkalaCategoryValue(question.skalaCategory || '');
                            setShowSkalaCategoryInput(true);
                          }}
                          style={{ cursor: 'pointer' }}
                          variant='light'
                          size='sm'
                        >
                          Skala: {question.skalaCategory}
                        </Badge>
                      ) : (
                        <Button
                          variant='subtle'
                          color='indigo'
                          size='xs'
                          onClick={() => setShowSkalaCategoryInput(true)}
                          style={{ cursor: 'pointer' }}
                          leftSection={<IconCategoryPlus size={16} />}
                        >
                          <Text size='xs' fw={'bold'}>
                            Add Skala
                          </Text>
                        </Button>
                      )}
                    </>
                  )}
                </Group>
                {question.description && (
                  <Text size='sm' color='dimmed' mt={6}>
                    {question.description}
                  </Text>
                )}

                {/* Range items display */}
                {question.type === 'Range' &&
                  question.rangeItems &&
                  question.rangeItems.length > 0 && (
                    <Box mt='xs'>
                      <Text size='sm' fw={500} color='dimmed' mb={8}>
                        Scale
                      </Text>
                      <Group gap={8}>
                        {[...question.rangeItems]
                          .sort((a, b) => (a.sequence || 0) - (b.sequence || 0))
                          .map((item: any, idx: number) => (
                            <Badge
                              key={idx}
                              size='sm'
                              variant='outline'
                              color={
                                idx === 0
                                  ? 'red'
                                  : idx === question.rangeItems!.length - 1
                                    ? 'green'
                                    : 'blue'
                              }
                            >
                              {item.label} ({item.value})
                            </Badge>
                          ))}
                      </Group>
                    </Box>
                  )}

                {/* Free-text display */}
                {question.type === 'Free-text' && (
                  <Box
                    mt='xs'
                    style={{
                      padding: '8px 12px',
                      borderRadius: '4px',
                      backgroundColor: '#f0f0f0',
                      fontStyle: 'italic'
                    }}
                  >
                    <Text size='xs' color='dimmed'>
                      Free-text response field
                    </Text>
                  </Box>
                )}
              </Stack>

              <ActionIcon
                color='red'
                onClick={(e) => {
                  e.stopPropagation();
                  onRemove(question.id, respType, moduleId, externalTypeId);
                }}
                disabled={isReset !== 'Y'}
                title={isReset !== 'Y' ? 'Cannot delete questions in this mode' : 'Delete question'}
              >
                <IconTrash size={16} />
              </ActionIcon>
            </Group>
          </Box>
        </div>
      )}
    </Draggable>
  );
};

export default QuestionItem;
